import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { environment } from '../../../../../../environments/environment';
import { TipoContatoEnum } from '../../../../../shared/enums/pessoa/tipo-contato.enum';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ContasPagarService } from '../../../../../shared/services/apis/financas/contas-pagar.service';
import { CategoriaFinanceira } from '../../../../../shared/services/models/financas/categoria-financeira.model';
import { CentroCusto } from '../../../../../shared/services/models/financas/centro-custo.model';
import { ContaFinanceira } from '../../../../../shared/services/models/financas/conta-financeira.model';
import { ContasPagarBulkEditRequest } from '../../../../../shared/services/requests/financas/contas-pagar.request';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { CustomDateAdapter } from '../../../../../shared/services/custom-date-adapter';

export interface EditarContasMassaDialogData {
  idsContasPagarParcela: number[];
  categoriasFinanceiras: CategoriaFinanceira[];
  contasFinanceiras: ContaFinanceira[];
  centrosCusto: CentroCusto[];
}

interface AnexoItem {
  nomeArquivo: string;
  tamanhoBytes: number;
  base64: string;
}

@Component({
  selector: 'app-editar-contas-massa-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    SelectPesquisavelComponent,
    AlertCardComponent,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './editar-contas-massa-dialog.component.html',
  styleUrls: ['./editar-contas-massa-dialog.component.css'],
})
export class EditarContasMassaDialogComponent implements OnInit {
  public model: FormGroup;
  public isLoading: boolean = false;

  public urlFornecedor = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Fornecedor}`;

  // Anexos
  public anexos: AnexoItem[] = [];
  public isHovering: boolean = false;
  public readonly MAX_FILE_SIZE = 2 * 1024 * 1024;
  public readonly MAX_TOTAL_SIZE = 10 * 1024 * 1024;

  constructor(
    private formBuilder: FormBuilder,
    private contasPagarService: ContasPagarService,
    private alertService: AlertService,
    public dialogRef: MatDialogRef<EditarContasMassaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditarContasMassaDialogData
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      idFornecedor: [null],
      dataCompetencia: [null],
      idCategoriaFinanceira: [null],
      idCentroCusto: [null],
      descricao: [null],
      observacaoHtml: [null],
      idContaFinanceira: [null],
    });
  }

  get idFornecedorControl(): FormControl {
    return this.model.get('idFornecedor') as FormControl;
  }

  public get qtdSelecionados(): number {
    return this.data.idsContasPagarParcela.length;
  }

  // ─── Upload ──────────────────────────────────────────────────────────

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
    }
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFiles(input.files);
      input.value = '';
    }
  }

  private processFiles(files: FileList): void {
    const currentTotalSize = this.anexos.reduce((total, f) => total + f.tamanhoBytes, 0);
    let newFilesSize = 0;
    for (let i = 0; i < files.length; i++) {
      newFilesSize += files[i].size;
    }
    if (currentTotalSize + newFilesSize > this.MAX_TOTAL_SIZE) {
      this.alertService.show('Aviso', 'O tamanho total dos anexos não pode exceder 10MB.', AlertType.warning);
      return;
    }
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > this.MAX_FILE_SIZE) {
        this.alertService.show(
          'Aviso',
          `O arquivo "${file.name}" excede o limite de 2MB.`,
          AlertType.warning
        );
        continue;
      }
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64Data = e.target.result.split(',')[1];
        this.anexos.push({ nomeArquivo: file.name, tamanhoBytes: file.size, base64: base64Data });
      };
      reader.readAsDataURL(file);
    }
  }

  public removeAnexo(index: number): void {
    this.anexos.splice(index, 1);
  }

  public limparAnexos(): void {
    this.anexos = [];
  }

  public formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // ─── Confirmar ───────────────────────────────────────────────────────

  public confirmar(): void {
    if (this.isLoading) return;

    const formValue = this.model.getRawValue();

    const request: ContasPagarBulkEditRequest = {
      idsContasPagarParcela: this.data.idsContasPagarParcela,
      idFornecedor: formValue.idFornecedor || null,
      dataCompetencia: formValue.dataCompetencia
        ? new Date(formValue.dataCompetencia).toISOString().split('T')[0]
        : null,
      idCategoriaFinanceira: formValue.idCategoriaFinanceira || null,
      idCentroCusto: formValue.idCentroCusto || null,
      descricao: formValue.descricao?.trim() || null,
      observacaoHtml: formValue.observacaoHtml?.trim() || null,
      idContaFinanceira: formValue.idContaFinanceira || null,
      anexos:
        this.anexos.length > 0
          ? this.anexos.map(a => ({ nomeArquivo: a.nomeArquivo, base64: a.base64 }))
          : null,
    };

    const algumCampoPreenchido =
      request.idFornecedor != null ||
      request.dataCompetencia != null ||
      request.idCategoriaFinanceira != null ||
      request.idCentroCusto != null ||
      request.descricao != null ||
      request.observacaoHtml != null ||
      request.idContaFinanceira != null ||
      (request.anexos != null && request.anexos.length > 0);

    if (!algumCampoPreenchido) {
      this.alertService.show(
        'Atenção',
        'Preencha ao menos um campo para realizar a edição em massa.',
        AlertType.warning
      );
      return;
    }

    this.isLoading = true;
    this.contasPagarService.editarEmMassa(request).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Atenção', res.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show(
          'Sucesso',
          `${this.qtdSelecionados} parcela(s) atualizada(s) com sucesso!`,
          AlertType.success
        );
        this.dialogRef.close(true);
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro inesperado', err.message || err, AlertType.error);
      },
    });
  }

  public cancelar(): void {
    this.dialogRef.close(false);
  }
}
